import * as React from "react";
import { cn } from "../../lib/utils";

/**
 * Surface container for grouped content (Shadcn-style card).
 * @param {Object} props
 * @param {string} [props.className]
 * @returns {JSX.Element}
 */
const Card = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "rounded-xl border border-border bg-ui-card text-ui-card-foreground shadow-sm",
      className
    )}
    {...props}
  />
));
Card.displayName = "Card";

/**
 * Top region of a card (title stack, actions).
 * @param {Object} props
 * @param {string} [props.className]
 * @returns {JSX.Element}
 */
const CardHeader = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex flex-col space-y-1.5 p-6", className)}
    {...props}
  />
));
CardHeader.displayName = "CardHeader";

/**
 * Primary heading for a card.
 * @param {Object} props
 * @param {string} [props.className]
 * @returns {JSX.Element}
 */
const CardTitle = React.forwardRef(({ className, ...props }, ref) => (
  // eslint-disable-next-line jsx-a11y/heading-has-content -- children via ...props
  <h3
    ref={ref}
    className={cn("font-semibold leading-none tracking-tight", className)}
    {...props}
  />
));
CardTitle.displayName = "CardTitle";

/**
 * Supporting text under the card title.
 * @param {Object} props
 * @param {string} [props.className]
 * @returns {JSX.Element}
 */
const CardDescription = React.forwardRef(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn("text-sm text-ui-muted-foreground", className)}
    {...props}
  />
));
CardDescription.displayName = "CardDescription";

/**
 * Main body of a card.
 * @param {Object} props
 * @param {string} [props.className]
 * @returns {JSX.Element}
 */
const CardContent = React.forwardRef(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("p-6 pt-0", className)} {...props} />
));
CardContent.displayName = "CardContent";

/**
 * Footer row for secondary actions or metadata.
 * @param {Object} props
 * @param {string} [props.className]
 * @returns {JSX.Element}
 */
const CardFooter = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex items-center p-6 pt-0", className)}
    {...props}
  />
));
CardFooter.displayName = "CardFooter";

export {
  Card,
  CardHeader,
  CardFooter,
  CardTitle,
  CardDescription,
  CardContent,
};
